import {Component, OnDestroy, OnInit, HostBinding, HostListener, ViewEncapsulation, AfterViewInit, OnChanges} from '@angular/core';
import {MediaObserver} from '@angular/flex-layout';
import * as _ from 'lodash';
import {Subject} from 'rxjs';
import {takeUntil} from 'rxjs/operators';
import {TranslateService} from '@ngx-translate/core';
import {AuthenticationService} from 'app/auth/service';
import {CoreSidebarService} from '@core/components/core-sidebar/core-sidebar.service';
import {CoreConfigService} from '@core/services/config.service';
import {CoreMediaService} from '@core/services/media.service';
import {User} from 'app/auth/models';
import {coreConfig} from 'app/app-config';
import {Router} from '@angular/router';
import {StorageService} from '../../../shared/services/storage.service';
import {AuthService} from '../../../shared/services/auth.service';
import {GlobalService} from '../../../shared/services/global.service';
import {UniversityService} from '../../../shared/services/university.service';
import {environment} from '../../../../environments/environment';
import {DataService} from '../../../shared/services/data.service';
import {AppConstants} from '../../../shared/constants/app-constant';

@Component({
    selector: 'app-navbar',
    templateUrl: './navbar.component.html',
    styleUrls: ['./navbar.component.scss'],
    encapsulation: ViewEncapsulation.None
})

export class NavbarComponent implements OnInit, OnDestroy {
    public horizontalMenu: boolean;
    public hiddenMenu: boolean;
    public coreConfig: any;
    public currentSkin: string;
    public prevSkin: string;
    public currentUser: User;
    public languageOptions: any;
    public navigation: any;
    public selectedLanguage: any;
    @HostBinding('class.fixed-top')
    public isFixed = false;
    @HostBinding('class.navbar-static-style-on-scroll')
    public windowScrolled = false;
    studentProfile: any = './assets/images/image-backgrounds/1600-1600.jpg';

    // Add .navbar-static-style-on-scroll on scroll using HostListener & HostBinding
    @HostListener('window:scroll', []) onWindowScroll() {
        if ((window.pageYOffset || document.documentElement.scrollTop || document.body.scrollTop > 100) && this.coreConfig.layout.navbar.type == 'navbar-static-top') {
            this.windowScrolled = true;
        } else if ((this.windowScrolled && window.pageYOffset) || document.documentElement.scrollTop || document.body.scrollTop < 10) {
            this.windowScrolled = false;
        }
    }

    // Private
    private _unsubscribeAll: Subject<any>;
    user: any;
    university: any;
    profileImage: any;

    constructor(
        private _router: Router,
        private _authenticationService: AuthenticationService,
        private _coreConfigService: CoreConfigService,
        private _coreMediaService: CoreMediaService,
        private _coreSidebarService: CoreSidebarService,
        private _mediaObserver: MediaObserver,
        public _translateService: TranslateService,
        private authService: AuthService,
        private router: Router,
        private globalService: GlobalService,
        private universityService: UniversityService,
        private dataService: DataService) {
        this._authenticationService.currentUser.subscribe(x => (this.currentUser = x));
        this.languageOptions = {
            en: {
                title: 'English',
                flag: 'us'
            },
            fr: {
                title: 'French',
                flag: 'fr'
            },
            de: {
                title: 'German',
                flag: 'de'
            },
            pt: {
                title: 'Portuguese',
                flag: 'pt'
            }
        };
        this._unsubscribeAll = new Subject();
    }

    /**
     * Toggle sidebar open
     *
     * @param key
     */
    toggleSidebar(key): void {
        this._coreSidebarService.getSidebarRegistry(key).toggleOpen();
    }

    /**
     * Set the language
     *
     * @param language
     */
    setLanguage(language): void {
        // Set the selected language for the navbar on change
        this.selectedLanguage = language;

        // Use the selected language id for translations
        this._translateService.use(language);
    }

    /**
     * Toggle Dark Skin
     */
    toggleDarkSkin() {
        // Get the current skin
        this._coreConfigService
            .getConfig()
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe(config => {
                this.currentSkin = config.layout.skin;
            });

        // Toggle Dark skin with prevSkin skin
        this.prevSkin = localStorage.getItem('prevSkin');

        if (this.currentSkin === 'dark') {
            this._coreConfigService.setConfig(
                {layout: {skin: this.prevSkin ? this.prevSkin : 'default'}},
                {emitEvent: true}
            );
        } else {
            localStorage.setItem('prevSkin', this.currentSkin);
            this._coreConfigService.setConfig({layout: {skin: 'dark'}}, {emitEvent: true});
        }
    }

    /**
     * Logout method
     */
    logOut() {
        this.authService.logout().subscribe(data => {
            let user = this.globalService.getSelf();
            StorageService.clearAll();
            StorageService.setItem('self', {'university_id': AppConstants.universityId, 'user_type': 'STUDENT'});
            this.router.navigate(['/auth/login']).then(() => {
                window.location.reload();
            });
        }, error => {
            this.router.navigate(['/auth/login']).then(() => {
                window.location.reload();
            });
        });
    }

    /**
     * On init
     */
    ngOnInit(): void {
        this.dataService.message$.subscribe(message => {
            this.studentProfile = message;
        });
        this.user = this.globalService.getSelf() ? this.globalService.getSelf() : StorageService.getItem('self');
        this._coreConfigService.config.pipe(takeUntil(this._unsubscribeAll)).subscribe(config => {
            this.coreConfig = config;
            this.horizontalMenu = config.layout.type === 'horizontal';
            this.hiddenMenu = config.layout.menu.hidden === true;
            this.currentSkin = config.layout.skin;
            // Fix: for vertical layout if default navbar fixed-top than set isFixed = true
            if (this.coreConfig.layout.type === 'vertical') {
                setTimeout(() => {
                    if (this.coreConfig.layout.navbar.type === 'fixed-top') {
                        this.isFixed = true;
                    }
                }, 0);
            }
        });

        // Horizontal Layout Only: Add class fixed-top to navbar below large screen
        if (this.coreConfig.layout.type == 'horizontal') {
            // On every media(screen) change
            this._coreMediaService.onMediaUpdate.pipe(takeUntil(this._unsubscribeAll)).subscribe(() => {
                const isFixedTop = this._mediaObserver.isActive('bs-gt-xl');
                this.isFixed = !isFixedTop;
            });
        }

        // Set the selected language from default languageOptions
        this.selectedLanguage = _.find(this.languageOptions, {
            id: this._translateService.currentLang
        });

        this.getUniversityDetail();
    }

    goToHome() {
        let url = this.university['university_detail'].url;
        if (url) {
            window.location.href = url;
        } else {
            this.router.navigate(['']).then(() => {
                window.location.reload();
            });
        }
    }

    getUniversityDetail() {
        if (this.user && this.user.university_id) {
            this.universityService.detailByLanding(this.user.university_id).subscribe(data => {
                this.university = data;
                if (data && data['university_detail'] && data['university_detail']['profile_image']) {
                    this.profileImage = data['university_detail']['profile_image'].url;
                }
            });
        }
    }

    navigateTo(url) {
        this.router.navigate([url]).then(() => {
            window.location.reload();
        });
    }

    /**
     * On destroy
     */
    ngOnDestroy(): void {
        // Unsubscribe from all subscriptions
        this._unsubscribeAll.next();
        this._unsubscribeAll.complete();
    }
}
